import React, { useCallback } from 'react'
import styles from './index.module.scss'
import { Spinner } from '../../pages/shared/Spinner'
import ReportChart from './ReportChart'
import QUERY_KEYS from '../../graphql/queryKeys'
import reportsDriverComparisonQuery from '../../graphql/queries/reportsDriverComparison'
import { ArrayParam, NumericArrayParam, StringParam, useQueryParams } from 'use-query-params'
import useQuery from '../../hooks/useQuery'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import reportsFiltersQuery from '../../graphql/queries/reportsFilters'
import ReportsFilterForm from './reports-filter-form'
import { COLORS } from '../../utilities/constants'

function DriverComparison () {
  const [queryParams, setQueryParams] = useQueryParams({
    startDate: StringParam,
    endDate: StringParam,
    years: NumericArrayParam,
    resourceTypes: ArrayParam,
    ticketTypes: ArrayParam,
    status: ArrayParam
  })
  // Curried function to make sure we are always using 'replace' update.
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { data: reportData, isFetching } = useQuery([
    QUERY_KEYS.reportsDriverComparison,
    queryParams.startDate || null,
    queryParams.endDate || null,
    queryParams.years?.length > 0 ? queryParams.years?.map((e) => Number(e)) : null,
    queryParams.resourceTypes?.length > 0 ? queryParams.resourceTypes : null,
    queryParams.ticketTypes?.length > 0 ? queryParams.ticketTypes : null,
    queryParams.status?.length > 0 ? queryParams.status : null
  ],
  reportsDriverComparisonQuery,
  {
    initialData: { reportsDriverComparison: [] },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch reports data')
    }
  })
  const { data: { reportsFilters = [] } = {}, isFetching: isFetchingReportsFilters } = useQuery(
    [QUERY_KEYS.reportsFilters],
    reportsFiltersQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      },
      placplaceholderData: {
        reportFilters: {
          resources: [], ticketTypes: [], drivers: []
        }
      }
    }
  )
  return (
    <div className={styles.reportsDiv}>
      <div>
        <ReportsFilterForm
          handleInputChange={setQueryParamsReplace}
          showOptions={['dateRange', 'years', 'resourceTypes', 'ticketTypes', 'status']}
          options={{
            resourceTypes: reportsFilters?.resources?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.name
            })) || [],
            ticketTypes: reportsFilters?.ticketTypes?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.name
            })) || []
          }}
          initialValues={queryParams}
          clearSearch={() => setQueryParamsReplace({
            startDate: undefined,
            endDate: undefined,
            years: undefined,
            resourceTypes: undefined,
            ticketTypes: undefined,
            status: undefined
          })}
        />
      </div>
      <div className={styles.reportCharts}>
        <Spinner isFetching={reportData === undefined || isFetching || isFetchingReportsFilters}>
          <div className={styles.chartContainer}>
            <ReportChart
              title='Total Tickets By Driver'
              categories={reportData?.reportsDriverComparison?.totalTickets?.map(driver => driver.driverName) || []}
              series={[
                {
                  name: 'Total Tickets',
                  data: reportData?.reportsDriverComparison?.totalTickets?.map(driver => driver.totalTickets) || [],
                  color: COLORS?.PRIMARY_DK
                }
              ]}
              horizontal={true}
              height={reportData?.reportsDriverComparison?.totalTickets?.map(driver => driver.driverName)?.length * 35 || 350}
            />
          </div>
          <div className={styles.chartContainer}>
            <ReportChart
              title='Average Tickets By Driver Per Day'
              categories={reportData?.reportsDriverComparison?.avgTickets?.map(driver => driver.driverName) || []}
              series={[
                {
                  name: 'Average',
                  data: reportData?.reportsDriverComparison?.avgTickets?.map(driver => driver.avgTicketsPerDay) || [],
                  color: COLORS?.PRIMARY
                }
              ]}
              horizontal={true}
              height={reportData?.reportsDriverComparison?.avgTickets?.map(driver => driver.driverName)?.length * 35 || 350}
            />
          </div>
        </Spinner>
      </div>
    </div>
  )
}

export default DriverComparison
